import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultReportManager } from 'core/report/ReportManager';
import { downloadCsv } from 'utils/DownloadUtils';
import { WeatherType } from 'core/limitation/Limitation';
import {
  formatConditionValue,
  getResultLabel,
  getUnmatchedReasonLabels
} from './weatherVerificationColumns';
import {
  dedupeConditionLabel,
  DIMENSION_TO_WEATHER_TYPE,
  WeatherVerificationCampaignOption,
  WeatherVerificationData,
  WeatherVerificationResult,
  WeatherVerificationStoreOption
} from 'core/report/WeatherVerification';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const MAX_QUERY_DAYS = 3;
export const SIZE_PER_PAGE = 50;
/** 表格資料列的 key —— 後端沒有單筆 id，用「整點 + 門市」組出唯一值。 */
export const KEY_COLUMN = 'key';

/** CSV 的天氣維度欄位，與表格上顯示的四欄一致（所見即所得）。 */
const CSV_DIMENSIONS: WeatherType[] = [
  WeatherType.WEATHER_CODE,
  WeatherType.TEMPERATURE,
  WeatherType.RELATIVE_HUMIDITY,
  WeatherType.UV_INDEX
];

const reportManager = new DefaultReportManager();

export type WeatherVerificationQueryOptions = {
  from: string;
  to: string;
  campaignId?: number;
  result?: WeatherVerificationResult;
  storeNums: string[];
};

export type WeatherVerificationTableRecord = {
  [KEY_COLUMN]: string;
} & WeatherVerificationData['records'][number];

/**
 * 依訂單走期算出預設查詢區間：走期內「最後 3 天」，且不超過今天。
 * 走期尚未開始時退回單日（走期起始日），API 會回空資料。
 */
const getDefaultDateRange = (startDate: string, endDate: string) => {
  const orderStart = moment(startDate).startOf('day');
  const orderEnd = moment(endDate).startOf('day');
  const today = moment().startOf('day');
  const to = moment.min(orderEnd, today);
  const from = moment.max(
    orderStart,
    moment(to).subtract(MAX_QUERY_DAYS - 1, 'days')
  );
  if (from.isAfter(to)) {
    return {
      from: orderStart.format(DATE_FORMAT),
      to: orderStart.format(DATE_FORMAT)
    };
  }
  return { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) };
};

/**
 * 時段篩選的選項。
 *
 * 必須在 render 時才呼叫 —— 若寫成模組層 const，`i18n.t` 會在 import 當下就求值，
 * 早於 `App` 依帳號語系呼叫 `changeLanguage`，label 會被凍結在開機語系。
 */
const getResultFilterOptions = (): SelectOptions[] => [
  { label: i18n.t('weatherVerification.filters.all'), value: '' },
  {
    label: i18n.t('weatherVerification.filters.delivered'),
    value: WeatherVerificationResult.DELIVERED
  },
  {
    label: i18n.t('weatherVerification.filters.weatherUnmatched'),
    value: WeatherVerificationResult.WEATHER_UNMATCHED
  },
  {
    label: i18n.t('weatherVerification.filters.deviceAbnormal'),
    value: WeatherVerificationResult.DEVICE_ABNORMAL
  }
];

export type WeatherVerificationTabModelData = ReturnType<
  typeof useWeatherVerificationTabModel
>;

/**
 * 機台投遞驗證 Tab 的 model。
 *
 * 沿用 `useOutdoorDeviceReportModel` 的做法：篩選用的 state 只負責畫面，
 * 按下「查詢」才 commit 進 `queryOptions`，明細只跟著 `queryOptions` 與頁碼重打。
 *
 * @param active 是否正停在這個 Tab。活動下拉、門市選項與明細都只有在 Tab 被開啟後
 *               才載入，避免使用者從沒切過來也打 API。
 *
 * 注意 Tab badge 的數字不在這裡 —— 它是「有綁天氣鎖定的活動數」，由上層從已載入的
 * 活動列表算出（見 `L1ObjectDetailModel`）。刻意不用這裡的 `campaignOptions.length`，
 * 因為那份清單跟著查詢區間走，會讓使用者改日期時 badge 數字跳動。
 */
export const useWeatherVerificationTabModel = (
  l1ObjectId: number,
  startDate: string,
  endDate: string,
  active: boolean
) => {
  const { loading, callAPIs } = useCallAPI();
  const defaultRange = useMemo(
    () => getDefaultDateRange(startDate, endDate),
    [startDate, endDate]
  );

  const [from, setFrom] = useState<string>(defaultRange.from);
  const [to, setTo] = useState<string>(defaultRange.to);
  const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
  const [storeNums, setStoreNums] = useState<string[]>([]);
  const [result, setResult] = useState<string>('');

  const [campaignOptionRecords, setCampaignOptionRecords] = useState<
    WeatherVerificationCampaignOption[]
  >([]);
  const [storeOptionRecords, setStoreOptionRecords] = useState<
    WeatherVerificationStoreOption[]
  >([]);
  const [data, setData] = useState<WeatherVerificationData | undefined>(
    undefined
  );
  const [page, setPage] = useState<number>(1);
  const [campaignsLoaded, setCampaignsLoaded] = useState<boolean>(false);

  const [queryOptions, setQueryOptions] =
    useState<WeatherVerificationQueryOptions>({
      from: defaultRange.from,
      to: defaultRange.to,
      campaignId: undefined,
      result: undefined,
      storeNums: []
    });

  const dayRangeError = useMemo(() => {
    const diffDays = moment(to).diff(moment(from), 'days') + 1;
    if (diffDays > MAX_QUERY_DAYS) {
      return i18n.t('weatherVerification.errors.maxQueryDays', {
        days: MAX_QUERY_DAYS
      });
    }
    return undefined;
  }, [from, to]);

  const resultFilterOptions = useMemo(getResultFilterOptions, []);

  const campaignOptions: SelectOptions[] = useMemo(
    () =>
      campaignOptionRecords.map((record) => ({
        label: record.campaignName,
        value: record.campaignId
      })),
    [campaignOptionRecords]
  );

  const storeOptions: SelectOptions[] = useMemo(
    () =>
      storeOptionRecords.map((record) => ({
        label: record.storeName,
        value: record.storeNum
      })),
    [storeOptionRecords]
  );

  // 活動下拉：與門市、明細一致，只有 Tab 被開啟後才載入。
  useEffect(() => {
    if (!active || dayRangeError) {
      return;
    }
    callAPIs(
      [
        () =>
          reportManager.getWeatherVerificationCampaigns(l1ObjectId, from, to)
      ],
      (records: WeatherVerificationCampaignOption[]) => {
        setCampaignOptionRecords(records);
        setCampaignsLoaded(true);
        // 沿用目前選的活動；若它不在新區間的清單裡就退回第一筆。
        setCampaignId((prev) =>
          prev && records.some((record) => record.campaignId === prev)
            ? prev
            : records[0]?.campaignId
        );
      }
    );
    // dayRangeError 只是 from/to 的衍生值，不需要進依賴
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, l1ObjectId, from, to, callAPIs]);

  // 門市選項：跟著活動與區間走。
  useEffect(() => {
    if (!active || !campaignId || dayRangeError) {
      return;
    }
    callAPIs(
      [
        () =>
          reportManager.getWeatherVerificationStores(
            l1ObjectId,
            campaignId,
            from,
            to
          )
      ],
      (records: WeatherVerificationStoreOption[]) => {
        setStoreOptionRecords(records);
        // 換活動後，已選門市可能不存在於新清單，濾掉避免送出無效的 storeNums。
        setStoreNums((prev) =>
          prev.filter((storeNum) =>
            records.some((record) => record.storeNum === storeNum)
          )
        );
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, l1ObjectId, campaignId, from, to, callAPIs]);

  // 明細：只跟著已 commit 的 queryOptions 與頁碼。
  useEffect(() => {
    if (!active || !queryOptions.campaignId) {
      setData(undefined);
      return;
    }
    callAPIs(
      [
        () =>
          reportManager.getWeatherVerificationData({
            l1ObjectId,
            campaignId: queryOptions.campaignId as number,
            from: queryOptions.from,
            to: queryOptions.to,
            result: queryOptions.result,
            storeNums: queryOptions.storeNums,
            page: page - 1 // 畫面 1-based，API 0-based
          })
      ],
      (response: WeatherVerificationData) => setData(response)
    );
  }, [active, l1ObjectId, queryOptions, page, callAPIs]);

  // 活動下拉載入後把預設值帶進 queryOptions，讓使用者切過來就有資料。
  useEffect(() => {
    if (!campaignId || queryOptions.campaignId) {
      return;
    }
    setQueryOptions((prev) => ({ ...prev, campaignId }));
  }, [campaignId, queryOptions.campaignId]);

  const onDateRangeChange = useCallback((newFrom = '', newTo = '') => {
    setFrom(moment(newFrom).format(DATE_FORMAT));
    setTo(moment(newTo).format(DATE_FORMAT));
  }, []);

  const onResultChange = useCallback((value: string | number) => {
    setResult(value as string);
  }, []);

  const onCampaignChange = useCallback((value: number | undefined) => {
    setCampaignId(value ? +value : undefined);
    setStoreNums([]);
  }, []);

  const onStoreChange = useCallback((values: string[]) => {
    setStoreNums(values ?? []);
  }, []);

  const onQueryBtnClick = useCallback(() => {
    if (!campaignId) {
      return;
    }
    setPage(1);
    setQueryOptions({
      from,
      to,
      campaignId,
      // 空字串代表「全部時段」，轉成 undefined 讓 API 層不帶這個參數。
      result: result ? (result as WeatherVerificationResult) : undefined,
      storeNums
    });
  }, [from, to, campaignId, result, storeNums]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const tableData: WeatherVerificationTableRecord[] = useMemo(
    () =>
      (data?.records ?? []).map((record) => ({
        ...record,
        [KEY_COLUMN]: `${record.dataTime}_${record.spaceId}`
      })),
    [data]
  );

  /**
   * 匯出 CSV —— spec 要求「所見即所得」，所以只匯出當前這一頁的 50 筆，不額外撈全量。
   * 欄位：時間、門市、活動名稱、結果、各天氣條件實際值、各天氣條件設定值、不符合原因。
   */
  const onExportCsv = useCallback(() => {
    if (tableData.length === 0) {
      return;
    }
    const campaignName = data?.campaign?.campaignName ?? '';
    const dimensionLabel = (weatherType: WeatherType) =>
      i18n.t(`limitation.labels.${weatherType}`);

    const headers = [
      i18n.t('weatherVerification.headers.dataTime'),
      i18n.t('weatherVerification.headers.storeName'),
      i18n.t('weatherVerification.labels.campaign'),
      i18n.t('weatherVerification.headers.result'),
      ...CSV_DIMENSIONS.map(dimensionLabel),
      ...CSV_DIMENSIONS.map((weatherType) =>
        i18n.t('weatherVerification.headers.targetOf', {
          dimension: dimensionLabel(weatherType)
        })
      ),
      i18n.t('weatherVerification.headers.unmatchedReasons')
    ];

    const rows = tableData.map((record) => [
      moment(record.dataTime).format('MM/DD HH:00'),
      record.storeName,
      campaignName,
      getResultLabel(record),
      ...CSV_DIMENSIONS.map((weatherType) =>
        formatConditionValue(weatherType, record.conditions?.[weatherType])
      ),
      ...CSV_DIMENSIONS.map((weatherType) =>
        dedupeConditionLabel(record.conditions?.[weatherType]?.target?.label)
      ),
      getUnmatchedReasonLabels(record.unmatchedReasons).join(',')
    ]);

    const fileName = i18n.t('weatherVerification.labels.csvFileName', {
      campaign: campaignName,
      from: queryOptions.from,
      to: queryOptions.to,
      page
    });
    downloadCsv(fileName, headers, rows);
  }, [tableData, data, queryOptions.from, queryOptions.to, page]);

  /** Campaign header 的條件摘要，例：`天氣現象：晴天 ｜ 溫度：≥28°C`。未設定的維度略過。 */
  const campaignConditionSummary = useMemo(() => {
    const conditions = data?.campaign?.conditions ?? [];
    if (conditions.length === 0) {
      return i18n.t('weatherVerification.labels.noConditions');
    }
    return conditions
      .map((condition) => {
        const weatherType = DIMENSION_TO_WEATHER_TYPE[condition.dimension];
        const dimensionLabel = weatherType
          ? i18n.t(`limitation.labels.${weatherType}`)
          : condition.dimension;
        return `${dimensionLabel}：${dedupeConditionLabel(condition.label)}`;
      })
      .join(' ｜ ');
  }, [data]);

  return {
    loading,
    from,
    to,
    result,
    campaignId,
    storeNums,
    resultFilterOptions,
    campaignOptions,
    storeOptions,
    campaignsLoaded,
    dayRangeError,
    page,
    totalSize: data?.total ?? 0,
    campaign: data?.campaign,
    campaignConditionSummary,
    tableData,
    canQuery: !dayRangeError && !!campaignId,
    onDateRangeChange,
    onResultChange,
    onCampaignChange,
    onStoreChange,
    onQueryBtnClick,
    onPageChange,
    onExportCsv,
    canExportCsv: tableData.length > 0
  };
};
